import { CheckIcon, PaletteIcon } from "lucide-react";

import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  DASHBOARD_HEADER_COLOR_ORDER,
  dashboardHeaderColorToLabel,
  dashboardHeaderColorToSwatchClassName,
} from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import { DashboardHeaderColor } from "@/types";

export const DashboardHeaderBackgroundPicker = ({
  className,
  disabled = false,
  color,
  onColorChange,
}: {
  className?: string;
  disabled?: boolean;
  color: DashboardHeaderColor;
  onColorChange: (color: DashboardHeaderColor) => void;
}) => {
  /* Handlers */
  const handleColorSelect = (next: DashboardHeaderColor) => {
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.DASHBOARD,
      "Header background color",
      next,
    );
    onColorChange(next);
  };

  /* Render */
  const render = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          className={cn(
            "h-8 w-8 disabled:pointer-events-none disabled:opacity-40",
            className,
          )}
          disabled={disabled}
          size="icon"
          type="button"
          variant="ghost"
        >
          <PaletteIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[260px]">
        <DropdownMenuLabel>Color</DropdownMenuLabel>
        {DASHBOARD_HEADER_COLOR_ORDER.map((option) => (
          <DropdownMenuItem
            key={option}
            onSelect={() => {
              handleColorSelect(option);
            }}
          >
            <Row className="w-full items-center gap-2">
              <span
                className={cn(
                  "h-4 w-4 shrink-0 rounded-full border border-slate-200",
                  dashboardHeaderColorToSwatchClassName[option],
                )}
              />
              <Text className="min-w-0 flex-1 text-sm">
                {dashboardHeaderColorToLabel[option]}
              </Text>
              {color === option ? (
                <CheckIcon className="h-4 w-4 shrink-0 text-slate-600" />
              ) : null}
            </Row>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return render();
};
